import { NO_IMAGE, ROUTE_PATHS, S3_URL } from '@/constants';
import { useLogoutMutation } from '@/services';
import useAuthStore, { resetState } from '@/stores/authStore';
import { idbStorage } from '@/utils/idbStorage';
import { Menu } from '@headlessui/react';
import { LogOut, Settings, User, Shield } from 'lucide-react';
import { useTransition } from 'react';
import { useNavigate } from 'react-router-dom';

const UserMenu = () => {
  const navigate = useNavigate();
  const { user }: Record<string, any> = useAuthStore(
    state => state.userInfo ?? {}
  );

  const { mutateAsync: LogOutMutation } = useLogoutMutation();
  const [, startTransition] = useTransition();

  const handleLogout = async () => {
    startTransition(async () => {
      const res = await LogOutMutation();
      const { success } = res;

      if (success) {
        resetState();
        await idbStorage.removeItem('auth-store');
        window.location.reload();
      }
    });
  };

  return (
    <Menu as='div' className='relative'>
      <Menu.Button className='flex items-center space-x-3 p-2 rounded-2xl hover:bg-slate-50 transition-all duration-200 group'>
        <div className='h-10 w-10 gradient-primary rounded-full flex items-center justify-center ring-2 ring-slate-200 group-hover:ring-blue-300 transition-all duration-200'>
          <img
            src={user.image ? S3_URL + user.image : S3_URL + NO_IMAGE}
            alt={user?.name ?? ''}
            className='h-10 w-10 rounded-full object-cover bg-slate-100'
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=f1f5f9&color=64748b&bold=true`;
            }}
          />
        </div>
        <div className='text-sm text-left hidden sm:block'>
          <div className='font-semibold text-slate-900'>
            {user?.name || 'Placeholder User'}
          </div>
          <div className='text-slate-500 font-medium'>
            {user?.username || 'Placeholder Role'}
          </div>
        </div>
      </Menu.Button>
      <Menu.Items className='absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200/60 z-50 p-0'>
        <div className='py-2'>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() => navigate(ROUTE_PATHS.EDIT_PROFILE)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-base font-semibold rounded-none transition-colors ${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'}`}
              >
                <User className='h-5 w-5' />
                Edit profile
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() => navigate(ROUTE_PATHS.CHANGE_PASSWORD)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-base font-semibold rounded-none transition-colors ${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'}`}
              >
                <Shield className='h-5 w-5' />
                Change password
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() => navigate(ROUTE_PATHS.CONFIGURATION)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-base font-semibold rounded-none transition-colors ${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'}`}
              >
                <Settings className='h-5 w-5' />
                Configuration
              </button>
            )}
          </Menu.Item>
        </div>
        <div className='border-t border-slate-100'></div>
        <Menu.Item>
          {({ active }) => (
            <button
              onClick={handleLogout}
              className={`w-full flex items-center gap-3 px-6 py-3 text-base font-semibold rounded-none rounded-b-2xl transition-colors ${active ? 'bg-red-50 text-red-700' : 'text-slate-700'}`}
            >
              <LogOut className='h-5 w-5' />
              Sign out
            </button>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
};

export default UserMenu;
